<h1 mat-dialog-title>
  <div class="inline-flex items-center w-full">
    <span class="flex-auto">{{
      data.mode === 'view' ? 'Event details' : 'Interactie inplannen'
    }}</span>

    @if (data.mode === 'edit') {
      <button
        *appHasPermission="'client_event:delete'"
        mat-icon-button
        matTooltip="Event verwijderen"
        (click)="markForDeletionAndConfirm()"
        type="button"
      >
        <mat-icon>delete</mat-icon>
      </button>
    }
  </div>
</h1>

<mat-dialog-content class="scrollbar-width-thin">
  <form [formGroup]="form" class="flex flex-col gap-4" id="event-dialog-form-id">
    @if (data.mode === 'create' && canUseMedia()) {
      <div class="space-y-2">
        <span class="block font-medium text-sm select-none">Event type</span>
        <mat-radio-group
          [value]="eventType()"
          (change)="handleEventTypeChange($event.value)"
        >
          <mat-radio-button value="interaction">Interactie</mat-radio-button>
          <mat-radio-button value="media-image">Afbeelding</mat-radio-button>
          <mat-radio-button value="media-audio">Audio</mat-radio-button>
        </mat-radio-group>
      </div>
    }

    @if (!isMediaEvent()) {
    <div class="flex items-center justify-between gap-2">
      <div class="space-y-2">
        <span class="block font-medium text-sm select-none">Gekozen interactie</span>
        <div class="flex items-center gap-2">
          @if (selectedInteraction(); as interaction) {
            <span class="text-base w-min max-w-40 truncate block"
              >{{ interaction.label }}
            </span>
            <span
              class="bg-gray-300 text-gray-800 py-1 px-2 rounded-full text-xs font-medium"
            >
              v{{ interaction.version }}
            </span>
            @if (isTemplate()) {
              <span
                class="bg-blue-100 text-blue-800 py-1 px-2 rounded-full text-xs font-medium"
              >
                Template
              </span>
            }
            <button
              *appHasPermission="'interaction:read'"
              mat-icon-button
              matTooltip="Bekijk flow"
              type="button"
              (click)="openFlowView()"
            >
              <mat-icon>account_tree</mat-icon>
            </button>
          } @else {
            <span class="text-base">Geen</span>
            @if (data.mode === 'create') {
              <span class="text-base text-gray-400">—</span>
              <a
                *appHasPermission="'interaction:create'"
                [href]="createInteractionHref"
                (click)="handleCreateInteraction($event)"
                [class.pointer-events-none]="creating() || submitted()"
                [class.opacity-50]="creating() || submitted()"
                class="text-sm text-blue-700 hover:text-blue-900"
                tabindex="-1"
              >
                Interactie aanmaken
              </a>
            }
          }
        </div>
      </div>
    @if (data.mode === 'create') {
      @if (canReadInteractions()) {
        <button
          mat-stroked-button
          type="button"
          (click)="handleInteractionSelectionDialog()"
        >
          Kies interactie
        </button>
      } @else {
        <span class="text-xs text-gray-500 max-w-48 text-right">
          Om een interactie te kiezen heb je het recht "{{ interactionReadLabel }}" nodig.
        </span>
      }
    }
    </div>
    } @else {
    <div class="flex items-center justify-between gap-2">
      <div class="space-y-2">
        <span class="block font-medium text-sm select-none">Gekozen media</span>
        <div class="flex items-center gap-2">
          @if (selectedMedia(); as media) {
            <mat-icon class="text-base! size-4! shrink-0">{{
              media.type === 'image' ? 'image' : 'music_note'
            }}</mat-icon>
            <span class="text-base max-w-40 truncate block">{{ media.label }}</span>
          } @else {
            <span class="text-base">Geen</span>
          }
        </div>
      </div>
      @if (data.mode === 'create') {
        <button
          mat-stroked-button
          type="button"
          (click)="handleMediaSelectionDialog()"
        >
          Kies media
        </button>
      }
    </div>
    }

    @if (isTemplate()) {
      <div
        class="flex items-start gap-2 rounded-lg bg-blue-100 px-3 py-2 text-blue-950 text-xs"
      >
        <mat-icon class="text-blue-950 text-base! size-4! leading-4 shrink-0"
          >info</mat-icon
        >
        <span
          >Deze template wordt als nieuwe interactie aan de cliënt gekoppeld en
          ingepland.</span
        >
      </div>
    }

    @if (isTemplateDuplicate()) {
      <div
        class="flex items-start gap-2 rounded-lg bg-orange-100 px-3 py-2 text-orange-950 text-xs"
      >
        <mat-icon class="text-orange-950 text-base! size-4! leading-4 shrink-0"
          >warning</mat-icon
        >
        <span
          >Deze template is al eerder toegevoegd aan deze cliënt. Het is mogelijk om deze
          nogmaals toe te voegen, maar er wordt een nieuwe kopie gemaakt.</span
        >
      </div>
    }

    <div class="space-y-2">
      <label class="block font-medium text-sm select-none" for="start">Start</label>

      <div class="max-w-80">
        <mat-form-field subscriptSizing="dynamic" class="w-full">
          <input
            id="start"
            formControlName="start"
            matInput
            readonly
            [matDatepicker]="startDatePicker"
            [min]="now"
            (click)="startDatePicker.open()"
          />
          <mat-datepicker #startDatePicker />
        </mat-form-field>
      </div>
    </div>
    <div class="space-y-2">
      <span class="block font-medium text-sm select-none">Start/Eind tijd</span>

      <div class="flex items-center gap-2 max-w-80">
        <mat-form-field subscriptSizing="dynamic">
          <input
            matInput
            formControlName="startTime"
            [matTimepicker]="startTimePicker"
          />
          <mat-timepicker #startTimePicker interval="5m" />
        </mat-form-field>

        <span>&mdash;</span>

        <mat-form-field subscriptSizing="dynamic">
          <input
            matInput
            formControlName="endTime"
            [matTimepicker]="endTimePicker"
            [matTimepickerMin]="minEndTime()"
            (valueChange)="handleEndTimeChange($event)"
          />
          <mat-timepicker #endTimePicker interval="5m" />
        </mat-form-field>
      </div>
    </div>

    <ng-container formGroupName="recurrence">
      <div class="space-y-2">
        <label class="block font-medium text-sm select-none" for="recurrenceOption"
          >Herhaling</label
        >
        <mat-form-field subscriptSizing="dynamic">
          <mat-select
            [formControl]="recurrence"
            #recurrenceSelection
            id="recurrenceOption"
          >
            @for (option of defaultRecurrenceOptions; track $index) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      @if (recurrence.value !== 'none') {
        <div class="space-y-2">
          <label class="block font-medium text-sm select-none" for="intervalInput">Elke</label>
          <div class="flex items-center gap-2 max-w-53">
            <mat-form-field subscriptSizing="dynamic" class="w-full">
              <input
                id="intervalInput"
                matInput
                type="number"
                min="1"
                max="99"
                formControlName="interval"
                (input)="handleIntervalChange($any($event.target).valueAsNumber)"
              />
              <span class="text-sm pr-4 select-none" matSuffix>{{ intervalLabel() }}</span>
            </mat-form-field>
          </div>
        </div>
      }

      @if (recurrence.value === 'weekly') {
        <div class="space-y-2">
          <label class="block font-medium text-sm select-none" for="byWeekday"
            >Herhalen op</label
          >

          <div class="inline-flex gap-1">
            @for (weekDay of weekDays; track $index) {
              <div
                class="inline-flex items-center justify-center size-6 rounded-full bg-gray-200 hover:cursor-pointer not-[.active]:hover:bg-primary-700/[.32] [&.active]:bg-primary-700 [&.active]:text-primary-50 transition-colors"
                [class]="{
                  active: selectedWeekdays.isSelected(weekDay.value),
                }"
                matTooltipPosition="above"
                [matTooltip]="weekDay.longName"
                (click)="selectedWeekdays.toggle(weekDay.value)"
                role="presentation"
              >
                <span class="text-xs font-medium uppercase">{{
                  weekDay.shortName
                }}</span>
              </div>
            }
          </div>
        </div>
      }

      @if (recurrence.value === 'monthly') {
        <div class="space-y-2">
          <label class="block font-medium text-sm select-none" for="monthlyModeGroup"
            >Herhalen op</label
          >
          <mat-radio-group [formControl]="monthlyMode" id="monthlyModeGroup" class="flex flex-col gap-2">
            <mat-radio-button value="bymonthday">
              {{ monthlyByMonthdayLabel() }}
            </mat-radio-button>
            <mat-radio-button value="byweekday">
              {{ monthlyByWeekdayLabel() }}
            </mat-radio-button>
          </mat-radio-group>
        </div>
      }

      @if (recurrence.value !== 'none') {
        <div class="space-y-2">
          <label class="block font-medium text-sm select-none" for="byWeekday"
            >Eindigt op</label
          >

          <div class="max-w-80">
            <mat-form-field subscriptSizing="dynamic" class="w-full">
              <input
                formControlName="until"
                matInput
                readonly
                [min]="controls.start.value"
                [max]="getMaxUntilDate()"
                [matDatepicker]="untilDatePicker"
                (click)="untilDatePicker.open()"
              />
              <mat-datepicker #untilDatePicker />
            </mat-form-field>
          </div>
        </div>
      }
    </ng-container>
  </form>
</mat-dialog-content>

<mat-dialog-actions>
  @if (isViewMode) {
    <button mat-button type="button" [mat-dialog-close]="false">Sluiten</button>
  } @else {
    <button
      mat-button
      type="button"
      [mat-dialog-close]="false"
      [disabled]="submitted()"
    >
      Annuleren
    </button>

    <button
      mat-button
      (click)="handleConfirm()"
      [disabled]="submitted() || form.invalid || (isMediaEvent() ? !selectedMedia() : !selectedInteraction())"
      type="submit"
      form="event-dialog-form-id"
    >
      <mat-spinner
        class="!absolute inset-0 m-auto pointer-events-none transition-opacity ease-in-out"
        [diameter]="16"
        [class.opacity-100]="loading()"
        [class.opacity-0]="!loading()"
      ></mat-spinner>

      <span [class.opacity-0]="loading()" class="transition-opacity">Opslaan</span>
    </button>
  }
</mat-dialog-actions>
